import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step9/char.svg';
import { useEmploymentReadinessContext } from '../../hooks/use-form-context.hooks';
import {
  YesNoSomewhat,
  dispatchUpdateEmploymentReadiness,
} from '../../redux/slice/form-employment-readiness';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface FormData {
  agreeCanConductJobSearch: string;
  agreeKnowsStrengthsWeaknesses: string;
  agreeCollectsInformationAboutJobAndCompany: string;
  agreeHasPerformedInformationalInterviews: string;
  agreeMaintainsKnowledgeOnJob: string;
  agreeHasStrongReferences: string;
  agreeHasReviewedNetworkingStrategies: string;
}

interface ComponentProps {
  step: number;
  onBack: () => void;
}

const radio_options = [
  {
    label: 'Yes',
    value: YesNoSomewhat.YES,
  },
  {
    label: 'No',
    value: YesNoSomewhat.NO,
  },
  {
    label: 'Somewhat',
    value: YesNoSomewhat.SOMEWHAT,
  },
];
export const ReadinessAssessment: React.FC<ComponentProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    agreeCanConductJobSearch,
    agreeKnowsStrengthsWeaknesses,
    agreeCollectsInformationAboutJobAndCompany,
    agreeHasPerformedInformationalInterviews,
    agreeMaintainsKnowledgeOnJob,
    agreeHasStrongReferences,
    agreeHasReviewedNetworkingStrategies,
  } = useEmploymentReadinessContext();
  const [selectedJobSearchOption, setSelectedJobSearchOption] = useState(
    agreeCanConductJobSearch || '',
  );
  const [selectedStrengthWeaknessOption, setSelectedStrengthWeaknessOption] = useState(
    agreeKnowsStrengthsWeaknesses || '',
  );
  const [selectedJobProfileOption, setSelectedJobProfileOption] = useState(
    agreeCollectsInformationAboutJobAndCompany || '',
  );
  const [selectedPerformedInterviewOption, setSelectedPerformedInterviewOption] = useState(
    agreeHasPerformedInformationalInterviews || '',
  );
  const [selectedJobKnowledgeOption, setSelectedJobKnowledgeOption] = useState(
    agreeMaintainsKnowledgeOnJob || '',
  );
  const [selectedPunctual, setSelectedPunctual] = useState(agreeHasStrongReferences || '');
  const [selectedNetworkingStrategiesOption, setSelectedNetworkingStrategiesOption] = useState(
    agreeHasReviewedNetworkingStrategies || '',
  );
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    switch (field as keyof FormData) {
      case 'agreeCanConductJobSearch':
        setSelectedJobSearchOption(event.target.value);
        setError({ ...error, agreeCanConductJobSearch: '' });
        break;
      case 'agreeKnowsStrengthsWeaknesses':
        setSelectedStrengthWeaknessOption(event.target.value);
        setError({ ...error, agreeKnowsStrengthsWeaknesses: '' });
        break;
      case 'agreeCollectsInformationAboutJobAndCompany':
        setSelectedJobProfileOption(event.target.value);
        setError({ ...error, agreeCollectsInformationAboutJobAndCompany: '' });
        break;
      case 'agreeHasPerformedInformationalInterviews':
        setSelectedPerformedInterviewOption(event.target.value);
        setError({ ...error, agreeHasPerformedInformationalInterviews: '' });
        break;
      case 'agreeMaintainsKnowledgeOnJob':
        setSelectedJobKnowledgeOption(event.target.value);
        setError({ ...error, agreeMaintainsKnowledgeOnJob: '' });
        break;
      case 'agreeHasStrongReferences':
        setSelectedPunctual(event.target.value);
        setError({ ...error, agreeHasStrongReferences: '' });
        break;
      case 'agreeHasReviewedNetworkingStrategies':
        setSelectedNetworkingStrategiesOption(event.target.value);
        setError({ ...error, agreeHasReviewedNetworkingStrategies: '' });
        break;
    }
  };

  const [error, setError] = useState<FormData>({
    agreeCanConductJobSearch: '',
    agreeKnowsStrengthsWeaknesses: '',
    agreeCollectsInformationAboutJobAndCompany: '',
    agreeHasPerformedInformationalInterviews: '',
    agreeMaintainsKnowledgeOnJob: '',
    agreeHasStrongReferences: '',
    agreeHasReviewedNetworkingStrategies: '',
  });
  const { handleSubmit } = useForm<FormData>();
  const backButtonClick = () => {
    onBack();
  };
  const submitAssessment: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted employment readiness assessment form.', {
      selectedJobSearchOption,
      selectedStrengthWeaknessOption,
      selectedJobProfileOption,
      selectedPerformedInterviewOption,
      selectedJobKnowledgeOption,
      selectedPunctual,
      selectedNetworkingStrategiesOption,
    });

    const error_message: string = 'Please select an option';
    if (!selectedJobSearchOption) {
      setError({ ...error, agreeCanConductJobSearch: error_message });
    } else if (!selectedJobProfileOption) {
      setError({
        ...error,
        agreeCollectsInformationAboutJobAndCompany: error_message,
      });
    } else if (!selectedStrengthWeaknessOption) {
      setError({ ...error, agreeKnowsStrengthsWeaknesses: error_message });
    } else if (!selectedPerformedInterviewOption) {
      setError({
        ...error,
        agreeHasPerformedInformationalInterviews: error_message,
      });
    } else if (!selectedJobKnowledgeOption) {
      setError({ ...error, agreeMaintainsKnowledgeOnJob: error_message });
    } else if (!selectedPunctual) {
      setError({ ...error, agreeHasStrongReferences: error_message });
    } else if (!selectedNetworkingStrategiesOption) {
      setError({
        ...error,
        agreeHasReviewedNetworkingStrategies: error_message,
      });
    } else {
      const dto: Record<keyof FormData, YesNoSomewhat> = {
        agreeCanConductJobSearch: selectedJobSearchOption as YesNoSomewhat,
        agreeKnowsStrengthsWeaknesses: selectedStrengthWeaknessOption as YesNoSomewhat,
        agreeCollectsInformationAboutJobAndCompany: selectedJobProfileOption as YesNoSomewhat,
        agreeHasPerformedInformationalInterviews: selectedPerformedInterviewOption as YesNoSomewhat,
        agreeMaintainsKnowledgeOnJob: selectedJobKnowledgeOption as YesNoSomewhat,
        agreeHasStrongReferences: selectedPunctual as YesNoSomewhat,
        agreeHasReviewedNetworkingStrategies: selectedNetworkingStrategiesOption as YesNoSomewhat,
      };
      await dispatch(dispatchUpdateEmploymentReadiness(dto));
      navigate(`/pre-employment/employment-readiness/step${step + 1}`);
    }
  };

  useEffect(() => {
    if (agreeCanConductJobSearch) {
      setSelectedJobSearchOption(agreeCanConductJobSearch);
    }
    if (agreeKnowsStrengthsWeaknesses) {
      setSelectedStrengthWeaknessOption(agreeKnowsStrengthsWeaknesses);
    }
    if (agreeCollectsInformationAboutJobAndCompany) {
      setSelectedJobProfileOption(agreeCollectsInformationAboutJobAndCompany);
    }
    if (agreeHasPerformedInformationalInterviews) {
      setSelectedPerformedInterviewOption(agreeHasPerformedInformationalInterviews);
    }
    if (agreeMaintainsKnowledgeOnJob) {
      setSelectedJobKnowledgeOption(agreeMaintainsKnowledgeOnJob);
    }
    if (agreeHasStrongReferences) {
      setSelectedPunctual(agreeHasStrongReferences);
    }
    if (agreeHasReviewedNetworkingStrategies) {
      setSelectedNetworkingStrategiesOption(agreeHasReviewedNetworkingStrategies);
    }
  }, [
    agreeCanConductJobSearch,
    agreeKnowsStrengthsWeaknesses,
    agreeCollectsInformationAboutJobAndCompany,
    agreeHasPerformedInformationalInterviews,
    agreeMaintainsKnowledgeOnJob,
    agreeHasStrongReferences,
    agreeHasReviewedNetworkingStrategies,
  ]);

  return (
    <>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">10</div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold">Do you agree</span> with the following statements?
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-200px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className=" md:px-16"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAssessment)}
      >
        <ol style={{ listStyle: 'upper-latin' }} type="A">
          <li className="mb-5 ">
            I can conduct a job search.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedJobSearchOption === option.value}
                      onChange={(event) => handleRadioChange(event, 'agreeCanConductJobSearch')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeCanConductJobSearch"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeCanConductJobSearch && (
                <p className="text-red-500">{error.agreeCanConductJobSearch}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I evaluate my strengths and weaknesses before applying for a job.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedStrengthWeaknessOption === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'agreeKnowsStrengthsWeaknesses')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeKnowsStrengthsWeaknesses"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeKnowsStrengthsWeaknesses && (
                <p className="text-red-500">{error.agreeKnowsStrengthsWeaknesses}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I collect information about the job profile and the company which I am interested in.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedJobProfileOption === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'agreeCollectsInformationAboutJobAndCompany')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeCollectsInformationAboutJobAndCompany"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeCollectsInformationAboutJobAndCompany && (
                <p className="text-red-500">{error.agreeCollectsInformationAboutJobAndCompany}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I have performed{' '}
            <span className="tooltip text-purple-500 underline font-semibold">
              informational
              <span className="tooltiptext">
                An informational interview is an informal conversation with an individual who works
                in a company, role, or industry that interests you. Unlike job interviews, these
                conversations can help you gather information about a career path, not obtain a job.
                Informational interviews typically last 20 to 30 minutes, and you may also organize
                one to:
                <ul className="list-disc pl-5 mt-4">
                  <li>Discover new careers and fields</li>
                  <li>Recognize your suitability for a potential role</li>
                  <li>Learn the steps to pursue a potential career</li>
                  <li>Focus on specific career goals</li>
                  <li>Prepare for future job interviews</li>
                </ul>
              </span>
            </span>{' '}
            interviews.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPerformedInterviewOption === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'agreeHasPerformedInformationalInterviews')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeHasPerformedInformationalInterviews"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeHasPerformedInformationalInterviews && (
                <p className="text-red-500">{error.agreeHasPerformedInformationalInterviews}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I maintain up-to-date knowledge on the job I’m interested in.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedJobKnowledgeOption === option.value}
                      onChange={(event) => handleRadioChange(event, 'agreeMaintainsKnowledgeOnJob')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeMaintainsKnowledgeOnJob"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeMaintainsKnowledgeOnJob && (
                <p className="text-red-500">{error.agreeMaintainsKnowledgeOnJob}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I have a strong list of references and have explored strategies for strengthening this
            list.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPunctual === option.value}
                      onChange={(event) => handleRadioChange(event, 'agreeHasStrongReferences')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeHasStrongReferences"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeHasStrongReferences && (
                <p className="text-red-500">{error.agreeHasStrongReferences}</p>
              )}
            </div>
          </li>
          <li className="mb-5">
            I have reviewed networking strategies. I have explored personal contacts (ex-coworkers,
            ex-classmates, teachers, counsellors, etc.) and have communicated my job goal with them.
            <div className="rating-btns flex items-center mt-3">
              {radio_options.map((option) => (
                <div key={option.value} className="h-[55px] mr-5">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedNetworkingStrategiesOption === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'agreeHasReviewedNetworkingStrategies')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="agreeHasReviewedNetworkingStrategies"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
              {error.agreeHasReviewedNetworkingStrategies && (
                <p className="text-red-500">{error.agreeHasReviewedNetworkingStrategies}</p>
              )}
            </div>
          </li>
        </ol>
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </>
  );
};
